import React, { Suspense } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import RefundOrQuote from './components/account/refund_or_quote';
import { useAppFeatures } from './components/features/useAppFeatures';
import LayoutFrontaliere from './components/LayoutFrontaliere';
import { useGetActiveMaintenancesQuery } from './components/maintenance/maintenanceSlice';
import SignerPage from './components/subscription/formPro/entreprise/signer';
import {
  isAuthenticated,
  isPro,
  rightholder,
} from './utils/axiosApi';
import { isFrontaliere } from './utils/globalFunctions';
import LazyLoader from './utils/LazyLoader';

const Home = React.lazy(
  () => import(`./components/home/${process.env.REACT_APP_BRAND}`),
);
const Login = React.lazy(() => import('./components/login'));
const Subscription = React.lazy(
  () => import('./components/subscription'),
);

const FrontaliereForm = React.lazy(
  () => import('./components/subscription/form/frontaliere/index.js'),
);
const Dashboard = React.lazy(
  () => import('./components/account/dashboard/index.js'),
);
const FAQ = React.lazy(() => import('./components/account/faq'));
const Comptability = React.lazy(
  () => import('./components/account/admin/comptability'),
);
const Wallet = React.lazy(
  () => import('./components/account/admin/contracts'),
);
const Settings = React.lazy(
  () => import('./components/account/admin/settings'),
);
const DSN = React.lazy(
  () => import('./components/account/admin/settings/dsn'),
);
const ManageEmployee = React.lazy(
  () => import('./components/account/manage'),
);
const ManagePay = React.lazy(
  () => import('./components/account/pay'),
);
const Balance = React.lazy(
  () => import('./components/account/admin/comptability/balance'),
);
const Guarantees = React.lazy(
  () => import('./components/account/coverage'),
);
const MyRefundOrQuotes = React.lazy(
  () => import('./components/account/my_refunds_or_quotes'),
);
const Upgrade = React.lazy(
  () => import('./components/account/upgrade'),
);
const Advisor = React.lazy(
  () => import('./components/account/advisor'),
);
const Teams = React.lazy(() => import('./components/account/teams'));
const Documents = React.lazy(
  () => import('./components/account/documents'),
);
const Teleconsultation = React.lazy(
  () => import('./components/account/teleconsultation'),
);
const Cotisations = React.lazy(
  () => import('./components/account/cotisations'),
);
const AccountLayout = React.lazy(
  () => import('./components/TemplateLayoutAccount'),
);
const RightHolders = React.lazy(
  () => import('./components/account/rightholders'),
);
const PersonalInfo = React.lazy(
  () => import('./components/account/personal_info'),
);
const SubscriptionLayout = React.lazy(
  () => import('./components/SubscriptionLayout'),
);
const HomeLayout = React.lazy(
  () => import('./components/HomeLayout'),
);
const SmsAuthentication = React.lazy(
  () => import('./components/authentication/sms'),
);
const Politique = React.lazy(
  () => import('./components/PrivacyPolicy'),
);
const Mentions = React.lazy(() => import('./components/LegalNotice'));

const SignaturePage = React.lazy(
  () => import('./components/signature/SignaturePage'),
);

const FeatureGuard = ({ featureKey, children }) => {
  const { isEnabled } = useAppFeatures();
  if (!featureKey) {
    return children ? children : <Outlet />;
  }
  if (!isEnabled(featureKey)) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
};
export default function Router() {
  const role = isPro();
  const rightholderV = rightholder();
  const isMobileApp = process.env.REACT_APP_IS_MOBILE_APP === 'true';

  const { data } = useGetActiveMaintenancesQuery();
  const isMaintenanceActive = data?.active;

  const proRoutes = [
    {
      path: 'admin',
      element: <Outlet />,
      children: [
        { path: '', element: <Navigate to="dsn" replace /> },
        { path: 'dsn', element: <DSN /> },
        { path: 'settings', element: <Settings /> },
      ],
    },
    { path: 'manage', element: <ManageEmployee /> },
    { path: 'wallet', element: <Wallet /> },
    { path: 'pay', element: <ManagePay /> },
    {
      path: 'comptability',
      element: <Outlet />,
      children: [
        { path: '', element: <Navigate to="bills" replace /> },
        { path: 'bills', element: <Comptability /> },
        { path: 'balance', element: <Balance /> },
      ],
    },
    { path: 'coverage', element: <Guarantees /> },
    { path: 'teams', element: <Teams /> },
  ];

  const employeeRoutes = [
    {
      path: 'myrefunds',
      element: (
        <FeatureGuard featureKey="my_refunds">
          <MyRefundOrQuotes isQuotes={false} />
        </FeatureGuard>
      ),
    },
    {
      path: 'myquotes',
      element: (
        <FeatureGuard featureKey="my_refunds">
          <MyRefundOrQuotes isQuotes={true} />
        </FeatureGuard>
      ),
    },
    { path: 'faq', element: <FAQ /> },
    {
      path: 'refund',
      element: (
        <FeatureGuard featureKey="refund">
          <RefundOrQuote isQuote={false} rightholder={rightholderV} />
        </FeatureGuard>
      ),
    },
    {
      path: 'prices',
      element: (
        <FeatureGuard featureKey="cotisations">
          <Cotisations />
        </FeatureGuard>
      ),
    },
    {
      path: 'quote',
      element: (
        <FeatureGuard featureKey="quote">
          <RefundOrQuote isQuote={true} rightholder={rightholderV} />
        </FeatureGuard>
      ),
    },
    { path: 'upgrade', element: <Upgrade /> },
    {
      path: 'rightholders',
      element: (
        <FeatureGuard featureKey="rightholders">
          <RightHolders />
        </FeatureGuard>
      ),
    },
    {
      path: 'personal_info',
      element: (
        <FeatureGuard featureKey="personal_info">
          <PersonalInfo />
        </FeatureGuard>
      ),
    },
    {
      path: 'teleconsultation',
      element: (
        <FeatureGuard featureKey="teleconsultation">
          <Teleconsultation />
        </FeatureGuard>
      ),
    },
    {
      path: 'documents',
      element: (
        <FeatureGuard featureKey="documents">
          <Documents />
        </FeatureGuard>
      ),
    },
    {
      path: 'advisor',
      element: (
        <FeatureGuard featureKey="advisor">
          <Advisor asPro={role} />
        </FeatureGuard>
      ),
    },
  ];

  return (
    <Suspense fallback={<LazyLoader />}>
      {useRoutes([
        {
          path: '/sign',
          element: <SignaturePage />,
        },
        isFrontaliere
          ? {
              path: '/',
              element:
                isMobileApp && !isMaintenanceActive ? (
                  <Navigate to="/login" />
                ) : (
                  <Navigate to="/account" />
                ),
            }
          : {
              path: '/',
              element:
                isMobileApp && !isMaintenanceActive ? (
                  <Navigate to="/login" />
                ) : (
                  <HomeLayout asPro={role} />
                ),
              children: [
                { path: '/', element: <Home /> },
                { path: '/privacyPolicy', element: <Politique /> },
                { path: '/legalNotice', element: <Mentions /> },
              ],
            },
        !isMaintenanceActive && {
          path: '/login',
          element: !isAuthenticated() ? (
            isFrontaliere ? (
              <LayoutFrontaliere />
            ) : (
              <SubscriptionLayout />
            )
          ) : (
            <Navigate to="/account/" />
          ),
          children: [{ path: '/login', element: <Login /> }],
        },
        !isMaintenanceActive && {
          path: '/account',
          element: isAuthenticated() ? (
            <AccountLayout asPro={role} rightholder={rightholderV} />
          ) : (
            <Navigate to={isFrontaliere ? '/login' : '/'} replace />
          ),
          children: [
            {
              path: '',
              element: role ? (
                <Navigate to="manage" replace />
              ) : (
                <Dashboard />
              ),
            },
            { path: 'help', element: <Advisor asPro={role} /> },
            ...(role ? proRoutes : employeeRoutes),
          ],
        },
        ...(!isMobileApp && !isMaintenanceActive && isFrontaliere
          ? [
              {
                path: '/subscribe',
                element: <FrontaliereForm />,
              },
              { path: '/sms', element: <SmsAuthentication /> },
            ]
          : []),
        ...(!isMobileApp && !isMaintenanceActive && !isFrontaliere
          ? [
              {
                path: '/subscription',
                element: <SubscriptionLayout />,
                children: [
                  {
                    path: '/subscription/:step',
                    element: <Subscription />,
                  },
                  {
                    path: '/subscription',
                    element: <Subscription />,
                  },
                ],
              },
              { path: '/sms', element: <SmsAuthentication /> },
              {
                path: '/signer',
                element: <SubscriptionLayout />,
                children: [{ path: '', element: <SignerPage /> }],
              },
            ]
          : []),
        {
          path: '*',
          element: <Navigate to="/" replace />,
        },
      ])}
    </Suspense>
  );
}
